import { useEffect, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import {
  ArrowLeftIcon, ChevronLeftIcon, ChevronRightIcon, FunnelIcon,
} from '@heroicons/react/24/outline'
import { StarIcon as StarSolidIcon } from '@heroicons/react/24/solid'
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts'
import api from '../../services/api'

type RevenuePeriod = '1w' | '1m' | '6m' | '1y' | 'custom'

interface Plan {
  id: number
  slug: string
  name: string
  plan_type: string
  billing_period: string
  is_popular: boolean
  is_active: boolean
  price_min: number
  duration_days: number
}

interface Analytics {
  active_users: number
  past_users: number
  total_revenue: number
  revenue_trend: { month: string; revenue: number }[]
}

interface PaymentRow {
  id: number
  order_id: string
  payment_id: string | null
  amount: number
  currency: string
  status: string
  user_name: string | null
  user_email: string | null
  created_at: string | null
  captured_at: string | null
}

interface UserRow {
  subscription_id: number
  user_id: number
  user_name: string | null
  user_email: string | null
  status: string
  start_date: string | null
  end_date: string | null
  amount_paid: number | null
}

const PAGE_SIZE = 10

const ORDER_STATUS_META: Record<string, string> = {
  created: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
  captured: 'bg-green-500/10 text-green-400 border-green-500/20',
  failed: 'bg-red-500/10 text-red-400 border-red-500/20',
  refunded: 'bg-orange-500/10 text-orange-400 border-orange-500/20',
  authorized: 'bg-violet-500/10 text-violet-400 border-violet-500/20',
}

const SUB_STATUS_META: Record<string, string> = {
  active: 'bg-green-500/10 text-green-400 border-green-500/20',
  expired: 'bg-dark-700 text-gray-400 border-dark-600',
  cancelled: 'bg-red-500/10 text-red-400 border-red-500/20',
  suspended: 'bg-orange-500/10 text-orange-400 border-orange-500/20',
}

function StatusBadge({ status, meta }: { status: string; meta: Record<string, string> }) {
  const cls = meta[status] || 'bg-dark-700 text-gray-400 border-dark-600'
  return <span className={`text-xs px-2 py-0.5 rounded-full border font-medium capitalize ${cls}`}>{status}</span>
}

function Pagination({ page, total, onPage }: { page: number; total: number; onPage: (p: number) => void }) {
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE))
  if (total === 0) return null
  return (
    <div className="flex items-center justify-between px-4 py-3 border-t border-dark-700">
      <p className="text-xs text-gray-400">
        Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, total)} of {total}
      </p>
      <div className="flex items-center gap-2">
        <button onClick={() => onPage(Math.max(1, page - 1))} disabled={page === 1}
          className="w-8 h-8 rounded-lg border border-dark-600 bg-dark-900 flex items-center justify-center text-gray-300 hover:bg-dark-700 disabled:opacity-30 transition-colors">
          <ChevronLeftIcon style={{ width: 15, height: 15 }} />
        </button>
        <span className="text-xs text-gray-400">Page {page} of {totalPages}</span>
        <button onClick={() => onPage(Math.min(totalPages, page + 1))} disabled={page === totalPages}
          className="w-8 h-8 rounded-lg border border-dark-600 bg-dark-900 flex items-center justify-center text-gray-300 hover:bg-dark-700 disabled:opacity-30 transition-colors">
          <ChevronRightIcon style={{ width: 15, height: 15 }} />
        </button>
      </div>
    </div>
  )
}

export default function PlanDetailPage() {
  const { slug } = useParams<{ slug: string }>()
  const [plan, setPlan] = useState<Plan | null>(null)
  const [analytics, setAnalytics] = useState<Analytics | null>(null)
  const [analyticsLoading, setAnalyticsLoading] = useState(true)
  const [period, setPeriod] = useState<RevenuePeriod>('6m')
  const [customFrom, setCustomFrom] = useState('')
  const [customTo, setCustomTo] = useState('')

  const [tab, setTab] = useState<'transactions' | 'orders' | 'users'>('transactions')

  // Transactions
  const [txns, setTxns] = useState<PaymentRow[]>([])
  const [txnTotal, setTxnTotal] = useState(0)
  const [txnPage, setTxnPage] = useState(1)
  const [txnLoading, setTxnLoading] = useState(false)

  // Orders
  const [orders, setOrders] = useState<PaymentRow[]>([])
  const [orderTotal, setOrderTotal] = useState(0)
  const [orderPage, setOrderPage] = useState(1)
  const [orderStatusFilter, setOrderStatusFilter] = useState('')
  const [orderLoading, setOrderLoading] = useState(false)

  // Users
  const [users, setUsers] = useState<UserRow[]>([])
  const [userTotal, setUserTotal] = useState(0)
  const [userPage, setUserPage] = useState(1)
  const [userStatusFilter, setUserStatusFilter] = useState('')
  const [userLoading, setUserLoading] = useState(false)
  const planId = plan?.id

  useEffect(() => {
    api.get('/admin/plans').then(res => {
      const found = res.data.find((p: any) => p.slug === slug)
      if (found) setPlan(found)
    }).catch(() => {})
  }, [slug])

  const fetchAnalytics = async (p: RevenuePeriod, from?: string, to?: string) => {
    if (!planId) return
    setAnalyticsLoading(true)
    try {
      const params: Record<string, string> = { period: p }
      if (p === 'custom' && from && to) { params.date_from = from; params.date_to = to }
      const res = await api.get(`/admin/plans/${planId}/analytics`, { params })
      setAnalytics(res.data)
    } catch {}
    finally { setAnalyticsLoading(false) }
  }

  useEffect(() => {
    if (period !== 'custom') fetchAnalytics(period)
    else if (customFrom && customTo) fetchAnalytics('custom', customFrom, customTo)
  }, [period, customFrom, customTo, planId])

  const fetchTxns = async () => {
    if (!planId) return
    setTxnLoading(true)
    try {
      const res = await api.get(`/admin/plans/${planId}/transactions`, {
        params: { skip: (txnPage - 1) * PAGE_SIZE, limit: PAGE_SIZE },
      })
      setTxns(res.data.transactions)
      setTxnTotal(res.data.total)
    } catch {}
    finally { setTxnLoading(false) }
  }
  useEffect(() => { if (tab === 'transactions') fetchTxns() }, [tab, txnPage, planId])

  const fetchOrders = async () => {
    if (!planId) return
    setOrderLoading(true)
    try {
      const params: Record<string, any> = { skip: (orderPage - 1) * PAGE_SIZE, limit: PAGE_SIZE }
      if (orderStatusFilter) params.status = orderStatusFilter
      const res = await api.get(`/admin/plans/${planId}/orders`, { params })
      setOrders(res.data.orders)
      setOrderTotal(res.data.total)
    } catch {}
    finally { setOrderLoading(false) }
  }
  useEffect(() => { if (tab === 'orders') fetchOrders() }, [tab, orderPage, orderStatusFilter, planId])

  const fetchUsers = async () => {
    if (!planId) return
    setUserLoading(true)
    try {
      const params: Record<string, any> = { skip: (userPage - 1) * PAGE_SIZE, limit: PAGE_SIZE }
      if (userStatusFilter) params.status = userStatusFilter
      const res = await api.get(`/admin/plans/${planId}/users`, { params })
      setUsers(res.data.users)
      setUserTotal(res.data.total)
    } catch {}
    finally { setUserLoading(false) }
  }
  useEffect(() => { if (tab === 'users') fetchUsers() }, [tab, userPage, userStatusFilter, planId])

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <Link to="/admin/plans" className="inline-flex items-center gap-1.5 text-xs text-gray-400 hover:text-white transition-colors mb-2">
          <ArrowLeftIcon style={{ width: 14, height: 14 }} />
          Back to Plans
        </Link>
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-bold text-white">{plan?.name || 'Plan'}</h1>
          {plan?.is_popular && (
            <span className="text-xs px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-300 font-medium flex items-center gap-1">
              <StarSolidIcon style={{ width: 10, height: 10 }} /> Popular
            </span>
          )}
          {plan && !plan.is_active && (
            <span className="text-xs px-2 py-0.5 bg-dark-700 text-gray-500 rounded-full">Inactive</span>
          )}
        </div>
        {plan && (
          <p className="text-sm text-gray-400 mt-0.5">
            {plan.plan_type} · {plan.billing_period} · ₹{plan.price_min.toLocaleString('en-IN')} · {plan.duration_days} days
          </p>
        )}
      </div>

      {/* Stat tiles */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-dark-800 border border-dark-700 rounded-2xl p-4">
          <p className="text-2xl font-bold text-green-400">{analyticsLoading ? '—' : analytics?.active_users ?? 0}</p>
          <p className="text-xs text-gray-400 mt-0.5">Active Users</p>
        </div>
        <div className="bg-dark-800 border border-dark-700 rounded-2xl p-4">
          <p className="text-2xl font-bold text-gray-300">{analyticsLoading ? '—' : analytics?.past_users ?? 0}</p>
          <p className="text-xs text-gray-400 mt-0.5">Past Users</p>
        </div>
        <div className="bg-dark-800 border border-dark-700 rounded-2xl p-4">
          <p className="text-2xl font-bold text-white">
            ₹{analyticsLoading ? '—' : (analytics?.total_revenue ?? 0).toLocaleString('en-IN')}
          </p>
          <p className="text-xs text-gray-400 mt-0.5">Total Revenue</p>
        </div>
      </div>

      {/* Revenue chart */}
      <div className="bg-dark-800 border border-dark-700 rounded-2xl p-5">
        <div className="flex items-center justify-between mb-3">
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Revenue (actual paid, post-discount)</p>
          <div className="flex items-center gap-1 bg-dark-900 border border-dark-600 rounded-lg p-0.5">
            {(['1w', '1m', '6m', '1y', 'custom'] as RevenuePeriod[]).map(p => (
              <button key={p} onClick={() => setPeriod(p)}
                className={`text-[11px] font-semibold px-2 py-1 rounded-md transition-colors ${period === p ? 'bg-primary-500 text-white' : 'text-gray-400 hover:text-white'}`}>
                {p === 'custom' ? 'Custom' : p.toUpperCase()}
              </button>
            ))}
          </div>
        </div>

        {period === 'custom' && (
          <div className="flex items-center gap-2 mb-3">
            <input type="date" value={customFrom} onChange={e => setCustomFrom(e.target.value)}
              className="text-xs bg-dark-900 border border-dark-600 rounded-lg px-2 py-1.5 text-gray-200 focus:outline-none focus:ring-1 focus:ring-primary-500" />
            <span className="text-xs text-gray-500">to</span>
            <input type="date" value={customTo} onChange={e => setCustomTo(e.target.value)}
              className="text-xs bg-dark-900 border border-dark-600 rounded-lg px-2 py-1.5 text-gray-200 focus:outline-none focus:ring-1 focus:ring-primary-500" />
            <button onClick={() => customFrom && customTo && fetchAnalytics('custom', customFrom, customTo)}
              disabled={!customFrom || !customTo}
              className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-primary-500 text-white hover:bg-primary-600 disabled:opacity-40 transition-colors">
              Apply
            </button>
          </div>
        )}

        <div className="h-56">
          {analyticsLoading ? (
            <div className="h-full flex items-center justify-center text-xs text-gray-500">Loading…</div>
          ) : analytics && analytics.revenue_trend.some(t => t.revenue > 0) ? (
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={analytics.revenue_trend} margin={{ top: 5, right: 5, left: -10, bottom: 0 }}>
                <defs>
                  <linearGradient id="planDetailRevenueFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#f59e0b" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="#f59e0b" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#334155" vertical={false} />
                <XAxis dataKey="month" tick={{ fill: '#94a3b8', fontSize: 11 }} axisLine={{ stroke: '#334155' }} tickLine={false} />
                <YAxis tick={{ fill: '#94a3b8', fontSize: 11 }} axisLine={false} tickLine={false} width={50}
                  tickFormatter={(v) => v >= 1000 ? `₹${(v / 1000).toFixed(0)}k` : `₹${v}`} />
                <Tooltip
                  contentStyle={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 8, fontSize: 12 }}
                  labelStyle={{ color: '#e2e8f0' }}
                  formatter={(v: number) => [`₹${v.toLocaleString('en-IN')}`, 'Revenue']}
                />
                <Area type="monotone" dataKey="revenue" stroke="#f59e0b" strokeWidth={2} fill="url(#planDetailRevenueFill)" />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-full flex items-center justify-center text-xs text-gray-500">
              No revenue recorded in this period
            </div>
          )}
        </div>
      </div>

      {/* Transactions / Orders / Users tabs */}
      <div className="flex gap-1 bg-dark-800 border border-dark-700 rounded-xl p-1 w-fit">
        {([
          ['transactions', 'Transactions'],
          ['orders', 'Orders'],
          ['users', 'Users'],
        ] as [typeof tab, string][]).map(([key, label]) => (
          <button key={key} onClick={() => setTab(key)}
            className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-colors ${tab === key ? 'bg-dark-700 text-white' : 'text-gray-400 hover:text-gray-200'}`}>
            {label}
          </button>
        ))}
      </div>

      {/* Transactions tab — real money movements only */}
      {tab === 'transactions' && (
        <div className="bg-dark-800 rounded-2xl border border-dark-700 shadow-sm overflow-hidden">
          <p className="text-xs text-gray-500 px-4 pt-4">Captured and refunded payments only — real money movement.</p>
          <div className="overflow-x-auto mt-2">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-dark-700 text-left text-xs text-gray-400 uppercase tracking-wider">
                  <th className="px-4 py-3 font-medium">Date</th>
                  <th className="px-4 py-3 font-medium">User</th>
                  <th className="px-4 py-3 font-medium">Amount</th>
                  <th className="px-4 py-3 font-medium">Status</th>
                  <th className="px-4 py-3 font-medium">Order ID</th>
                  <th className="px-4 py-3 font-medium">Transaction ID</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-dark-700">
                {txnLoading ? (
                  <tr><td colSpan={6} className="text-center py-10 text-gray-400">Loading…</td></tr>
                ) : txns.length === 0 ? (
                  <tr><td colSpan={6} className="text-center py-10 text-gray-400">No transactions yet</td></tr>
                ) : txns.map(t => (
                  <tr key={t.id} className="hover:bg-dark-700 transition-colors">
                    <td className="px-4 py-3 text-gray-300 whitespace-nowrap">
                      {t.created_at ? new Date(t.created_at).toLocaleDateString('en-IN') : '—'}
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-gray-100">{t.user_name || '—'}</p>
                      <p className="text-xs text-gray-500">{t.user_email}</p>
                    </td>
                    <td className="px-4 py-3 text-gray-100 font-medium">₹{t.amount.toLocaleString('en-IN')}</td>
                    <td className="px-4 py-3"><StatusBadge status={t.status} meta={ORDER_STATUS_META} /></td>
                    <td className="px-4 py-3 font-mono text-xs text-gray-400">{t.order_id}</td>
                    <td className="px-4 py-3 font-mono text-xs text-gray-400">{t.payment_id || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pagination page={txnPage} total={txnTotal} onPage={setTxnPage} />
        </div>
      )}

      {/* Orders tab — every order attempt, any status */}
      {tab === 'orders' && (
        <div className="bg-dark-800 rounded-2xl border border-dark-700 shadow-sm overflow-hidden">
          <div className="flex items-center gap-2 px-4 pt-4 pb-2">
            <FunnelIcon style={{ width: 14, height: 14 }} className="text-gray-400" />
            <select
              value={orderStatusFilter}
              onChange={e => { setOrderStatusFilter(e.target.value); setOrderPage(1) }}
              className="text-xs bg-dark-900 border border-dark-600 text-gray-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-primary-500"
            >
              <option value="">All Statuses</option>
              <option value="created">Created</option>
              <option value="captured">Captured</option>
              <option value="failed">Failed</option>
              <option value="refunded">Refunded</option>
            </select>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-dark-700 text-left text-xs text-gray-400 uppercase tracking-wider">
                  <th className="px-4 py-3 font-medium">Date</th>
                  <th className="px-4 py-3 font-medium">User</th>
                  <th className="px-4 py-3 font-medium">Amount</th>
                  <th className="px-4 py-3 font-medium">Status</th>
                  <th className="px-4 py-3 font-medium">Order ID</th>
                  <th className="px-4 py-3 font-medium">Transaction ID</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-dark-700">
                {orderLoading ? (
                  <tr><td colSpan={6} className="text-center py-10 text-gray-400">Loading…</td></tr>
                ) : orders.length === 0 ? (
                  <tr><td colSpan={6} className="text-center py-10 text-gray-400">No orders found</td></tr>
                ) : orders.map(o => (
                  <tr key={o.id} className="hover:bg-dark-700 transition-colors">
                    <td className="px-4 py-3 text-gray-300 whitespace-nowrap">
                      {o.created_at ? new Date(o.created_at).toLocaleDateString('en-IN') : '—'}
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-gray-100">{o.user_name || '—'}</p>
                      <p className="text-xs text-gray-500">{o.user_email}</p>
                    </td>
                    <td className="px-4 py-3 text-gray-100 font-medium">₹{o.amount.toLocaleString('en-IN')}</td>
                    <td className="px-4 py-3"><StatusBadge status={o.status} meta={ORDER_STATUS_META} /></td>
                    <td className="px-4 py-3 font-mono text-xs text-gray-400">{o.order_id}</td>
                    <td className="px-4 py-3 font-mono text-xs text-gray-400">{o.payment_id || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pagination page={orderPage} total={orderTotal} onPage={setOrderPage} />
        </div>
      )}

      {/* Users tab */}
      {tab === 'users' && (
        <div className="bg-dark-800 rounded-2xl border border-dark-700 shadow-sm overflow-hidden">
          <div className="flex items-center gap-2 px-4 pt-4 pb-2">
            <FunnelIcon style={{ width: 14, height: 14 }} className="text-gray-400" />
            <select
              value={userStatusFilter}
              onChange={e => { setUserStatusFilter(e.target.value); setUserPage(1) }}
              className="text-xs bg-dark-900 border border-dark-600 text-gray-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-primary-500"
            >
              <option value="">All</option>
              <option value="active">Active</option>
              <option value="past">Past</option>
            </select>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-dark-700 text-left text-xs text-gray-400 uppercase tracking-wider">
                  <th className="px-4 py-3 font-medium">User</th>
                  <th className="px-4 py-3 font-medium">Status</th>
                  <th className="px-4 py-3 font-medium">Start</th>
                  <th className="px-4 py-3 font-medium">End</th>
                  <th className="px-4 py-3 font-medium">Amount Paid</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-dark-700">
                {userLoading ? (
                  <tr><td colSpan={5} className="text-center py-10 text-gray-400">Loading…</td></tr>
                ) : users.length === 0 ? (
                  <tr><td colSpan={5} className="text-center py-10 text-gray-400">No users found</td></tr>
                ) : users.map(u => (
                  <tr key={u.subscription_id} className="hover:bg-dark-700 transition-colors">
                    <td className="px-4 py-3">
                      <p className="text-gray-100">{u.user_name || '—'}</p>
                      <p className="text-xs text-gray-500">{u.user_email}</p>
                    </td>
                    <td className="px-4 py-3"><StatusBadge status={u.status} meta={SUB_STATUS_META} /></td>
                    <td className="px-4 py-3 text-gray-300 whitespace-nowrap">
                      {u.start_date ? new Date(u.start_date).toLocaleDateString('en-IN') : '—'}
                    </td>
                    <td className="px-4 py-3 text-gray-300 whitespace-nowrap">
                      {u.end_date ? new Date(u.end_date).toLocaleDateString('en-IN') : '—'}
                    </td>
                    <td className="px-4 py-3 text-gray-100">
                      {u.amount_paid != null ? `₹${u.amount_paid.toLocaleString('en-IN')}` : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pagination page={userPage} total={userTotal} onPage={setUserPage} />
        </div>
      )}
    </div>
  )
}
